import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../../contexts/ThemeContext';
import { bookingsAPI } from '../../../services/api';
import {
    Calendar as CalendarIcon,
    ChevronLeft,
    ChevronRight,
    ArrowLeft,
    Building2,
    ExternalLink,
    X,
    User,
    Clock,
    CalendarCheck,
    CalendarClock
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

const BookingCalendar = () => {
    const { t } = useTranslation();
    const { isDarkMode } = useTheme();
    const navigate = useNavigate();

    const [bookings, setBookings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedBooking, setSelectedBooking] = useState(null);

    const todayDate = new Date();
    const [currentMonth, setCurrentMonth] = useState(todayDate.getMonth()); // 0-11
    const [currentYear, setCurrentYear] = useState(todayDate.getFullYear());

    useEffect(() => {
        fetchBookings();
    }, []);

    const fetchBookings = async () => {
        try {
            setLoading(true);
            const response = await bookingsAPI.getAll({ status: 'pending', activeOnly: true });
            const response2 = await bookingsAPI.getAll({ status: 'confirmed', activeOnly: true });

            const active = [...(response.bookings || []), ...(response2.bookings || [])];
            setBookings(active);
        } catch (error) {
            console.error('Error fetching calendar bookings:', error);
            toast.error(t('error_loading_data'));
        } finally {
            setLoading(false);
        }
    };

    // Calendar logic
    const daysInMonth = (month, year) => new Date(year, month + 1, 0).getDate();
    const firstDayOfMonth = (month, year) => new Date(year, month, 1).getDay();

    const calendarGrid = useMemo(() => {
        const days = [];
        const totalDays = daysInMonth(currentMonth, currentYear);
        const firstDay = firstDayOfMonth(currentMonth, currentYear);

        // Previous month days to fill the first week
        const prevMonth = currentMonth === 0 ? 11 : currentMonth - 1;
        const prevYear = currentMonth === 0 ? currentYear - 1 : currentYear;
        const daysInPrevMonth = daysInMonth(prevMonth, prevYear);

        for (let i = firstDay - 1; i >= 0; i--) {
            days.push({
                day: daysInPrevMonth - i,
                month: prevMonth,
                year: prevYear,
                currentMonth: false
            });
        }

        // Current month days
        for (let i = 1; i <= totalDays; i++) {
            days.push({
                day: i,
                month: currentMonth,
                year: currentYear,
                currentMonth: true
            });
        }

        // Next month days to complete the 6-week grid
        const remaining = 42 - days.length;
        const nextMonth = currentMonth === 11 ? 0 : currentMonth + 1;
        const nextYear = currentMonth === 11 ? currentYear + 1 : currentYear;

        for (let i = 1; i <= remaining; i++) {
            days.push({
                day: i,
                month: nextMonth,
                year: nextYear,
                currentMonth: false
            });
        }

        return days;
    }, [currentMonth, currentYear]);

    const getBookingsForDay = (day, month, year) => {
        return bookings.filter(b => {
            const moveIn = new Date(b.moveInDate);
            return moveIn.getDate() === day &&
                moveIn.getMonth() === month &&
                moveIn.getFullYear() === year;
        });
    };

    const handlePrevMonth = () => {
        if (currentMonth === 0) {
            setCurrentMonth(11);
            setCurrentYear(currentYear - 1);
        } else {
            setCurrentMonth(currentMonth - 1);
        }
    };

    const handleNextMonth = () => {
        if (currentMonth === 11) {
            setCurrentMonth(0);
            setCurrentYear(currentYear + 1);
        } else {
            setCurrentMonth(currentMonth + 1);
        }
    };

    const handleGoToToday = () => {
        setCurrentMonth(todayDate.getMonth());
        setCurrentYear(todayDate.getFullYear());
    };

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center py-20 animate-pulse">
                <div className="h-12 w-12 border-4 border-red-500/20 border-t-red-600 rounded-full animate-spin mb-4"></div>
                <p className={`text-sm font-medium ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}>{t('loading')}...</p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header section with Premium Glassmorphism styling */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
                <div className="flex items-center gap-4">
                    <Link to="/admin/bookings" className={`p-2 rounded-lg border transition-colors ${isDarkMode ? 'text-zinc-400 bg-zinc-900 border-zinc-800 hover:text-white hover:bg-zinc-800' : 'text-gray-500 bg-white border-gray-200 hover:bg-gray-50'}`}>
                        <ArrowLeft size={18} />
                    </Link>
                    <div>
                        <h1 className={`text-2xl font-bold flex items-center ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                            <CalendarIcon className="mr-3 text-red-600" size={24} />
                            {t('booking_timeline')}
                        </h1>
                        <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-gray-400'}`}>
                            {t('monthly_view_holds')}
                        </p>
                    </div>
                </div>

                <div className="flex flex-wrap items-center gap-3">
                    <button
                        onClick={handleGoToToday}
                        className={`px-4 py-2 text-xs font-semibold rounded-lg border transition-colors 
                            ${isDarkMode ? 'bg-zinc-900 border-zinc-800 text-zinc-400 hover:text-white hover:bg-zinc-800' : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'}`}
                    >
                        {t('today')}
                    </button>

                    <div className={`flex items-center rounded-lg border overflow-hidden transition-colors ${isDarkMode ? 'border-zinc-800 bg-zinc-900' : 'border-gray-200 bg-white shadow-sm'}`}>
                        <button onClick={handlePrevMonth} className={`p-2 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors ${isDarkMode ? 'text-zinc-400 border-r border-zinc-800' : 'text-gray-500 border-r border-gray-100'}`}>
                            <ChevronLeft size={18} />
                        </button>
                        <div className={`px-4 py-2 text-xs font-bold min-w-[140px] text-center uppercase tracking-wider ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                            {new Date(currentYear, currentMonth).toLocaleString('default', { month: 'long', year: 'numeric' })}
                        </div>
                        <button onClick={handleNextMonth} className={`p-2 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors ${isDarkMode ? 'text-zinc-400 border-l border-zinc-800' : 'text-gray-500 border-l border-gray-100'}`}>
                            <ChevronRight size={18} />
                        </button>
                    </div>

                    <div className="hidden lg:flex items-center gap-2">
                        <select
                            value={currentMonth}
                            onChange={(e) => setCurrentMonth(parseInt(e.target.value))}
                            className={`px-3 py-2 rounded-lg border text-xs font-semibold outline-none transition-colors ${isDarkMode ? 'bg-zinc-900 border-zinc-800 text-white focus:border-red-600' : 'bg-white border-gray-200 text-gray-700 focus:border-red-600'}`}
                        >
                            {Array.from({ length: 12 }, (_, i) => {
                                const date = new Date(2000, i, 1);
                                return <option key={i} value={i}>{date.toLocaleString('default', { month: 'long' })}</option>;
                            })}
                        </select>

                        <select
                            value={currentYear}
                            onChange={(e) => setCurrentYear(parseInt(e.target.value))}
                            className={`px-3 py-2 rounded-lg border text-xs font-semibold outline-none transition-colors ${isDarkMode ? 'bg-zinc-900 border-zinc-800 text-white focus:border-red-600' : 'bg-white border-gray-200 text-gray-700 focus:border-red-600'}`}
                        >
                            {[0, 1, 2, 3, 4, 5].map((offset) => {
                                const year = todayDate.getFullYear() + offset - 2;
                                return <option key={year} value={year}>{year}</option>;
                            })}
                        </select>
                    </div>
                </div>
            </div>

            {/* Calendar Grid Container */}
            <div className={`relative rounded-xl border overflow-hidden ${isDarkMode ? 'bg-zinc-950 border-zinc-800' : 'bg-white border-gray-200'}`}>
                {/* Weekday headers - Clean & Professional */}
                <div className={`grid grid-cols-7 border-b ${isDarkMode ? 'border-zinc-800 bg-zinc-900/50' : 'border-gray-100 bg-gray-50'}`}>
                    {['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'].map(day => (
                        <div key={day} className={`py-3 text-center text-[10px] font-bold uppercase tracking-wider ${isDarkMode ? 'text-zinc-500' : 'text-gray-400'}`}>
                            {t(day)}
                        </div>
                    ))}
                </div>

                {/* Days grid - Responsive & Interactive */}
                <div className="grid grid-cols-7 auto-rows-[minmax(130px,1fr)]">
                    {calendarGrid.map((cell, idx) => {
                        const dayBookings = getBookingsForDay(cell.day, cell.month, cell.year);
                        const isToday = cell.day === todayDate.getDate() && cell.month === todayDate.getMonth() && cell.year === todayDate.getFullYear();
                        const isWeekend = idx % 7 === 0 || idx % 7 === 6;

                        return (
                            <div
                                key={idx}
                                className={`p-2 border-r border-b relative transition-colors
                                ${isDarkMode ? 'border-zinc-800/60' : 'border-gray-100'} 
                                ${cell.currentMonth ? '' : (isDarkMode ? 'bg-zinc-900/20' : 'bg-gray-50/40')}
                                ${isWeekend && cell.currentMonth ? (isDarkMode ? 'bg-zinc-900/30' : 'bg-gray-50/60') : ''}
                                ${isDarkMode ? 'hover:bg-zinc-900' : 'hover:bg-gray-50/50'}`}
                            >
                                <div className="flex justify-between items-start mb-1">
                                    <span className={`text-xs font-bold inline-flex items-center justify-center w-6 h-6 rounded-md
                                        ${!cell.currentMonth ? (isDarkMode ? 'text-zinc-700' : 'text-gray-300') : (isDarkMode ? 'text-zinc-500' : 'text-gray-500')}
                                        ${isToday ? 'bg-red-600 text-white !text-white' : ''}
                                    `}>
                                        {cell.day}
                                    </span>
                                </div>

                                <div className="space-y-1.5 overflow-hidden">
                                    {dayBookings.map(booking => (
                                        <div
                                            key={booking.id}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setSelectedBooking(booking);
                                            }}
                                            className={`px-2 py-1.5 rounded border-l-2 text-[10px] font-semibold cursor-pointer truncate transition-colors
                                                ${booking.status === 'confirmed'
                                                    ? (isDarkMode ? 'bg-blue-900/20 text-blue-300 border-blue-600' : 'bg-blue-50 text-blue-800 border-blue-600')
                                                    : (isDarkMode ? 'bg-amber-900/20 text-amber-300 border-amber-600' : 'bg-amber-50 text-amber-800 border-amber-600')
                                                }`}
                                        >
                                            <div className="flex items-center gap-1.5">
                                                <div className={`w-1.5 h-1.5 rounded-full ${booking.status === 'confirmed' ? 'bg-blue-500' : 'bg-amber-500'}`}></div>
                                                <span className="font-black uppercase tracking-tighter opacity-80">#{booking.apartmentNumber}</span>
                                                {booking.prospectName}
                                            </div>
                                        </div>
                                    ))}
                                    {dayBookings.length > 4 && (
                                        <p className="text-[9px] font-semibold text-center py-1 uppercase text-zinc-500">
                                            +{dayBookings.length - 4} {t('more')}
                                        </p>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Simple Legend & Quick Stats */}
            <div className={`flex flex-wrap items-center justify-between gap-6 px-6 py-4 rounded-xl border ${isDarkMode ? 'bg-zinc-900/50 border-zinc-800' : 'bg-white border-gray-200 shadow-sm'}`}>
                <div className="flex items-center gap-6">
                    <div className="flex items-center gap-2">
                        <div className={`w-3 h-3 rounded-sm bg-blue-600`}></div>
                        <span className={`text-[10px] font-bold uppercase tracking-wider ${isDarkMode ? 'text-zinc-500' : 'text-gray-500'}`}>
                            {t('confirmed')} {t('hold')}
                        </span>
                    </div>
                    <div className="flex items-center gap-2">
                        <div className={`w-3 h-3 rounded-sm bg-amber-600`}></div>
                        <span className={`text-[10px] font-bold uppercase tracking-wider ${isDarkMode ? 'text-zinc-500' : 'text-gray-500'}`}>
                            {t('pending')} {t('payment')}
                        </span>
                    </div>
                </div>

                <div className="flex items-center gap-4 text-[10px] uppercase font-bold tracking-wider">
                    <div className={`${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}>
                        <span className="text-blue-600 mr-1">{bookings.filter(b => b.status === 'confirmed').length}</span> {t('confirmed')}
                    </div>
                    <div className={`w-1 h-1 rounded-full bg-zinc-700/30`}></div>
                    <div className={`${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}>
                        <span className="text-amber-600 mr-1">{bookings.filter(b => b.status === 'pending').length}</span> {t('pending')}
                    </div>
                </div>
            </div>

            {/* Refined Booking Details Modal */}
            {selectedBooking && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 overflow-y-auto">
                    <div className="fixed inset-0 bg-zinc-950/60 backdrop-blur-sm" onClick={() => setSelectedBooking(null)}></div>
                    <div className={`relative w-full max-w-lg rounded-2xl shadow-xl overflow-hidden
                        ${isDarkMode ? 'bg-zinc-900 border border-zinc-800' : 'bg-white border border-gray-100'}`}>

                        {/* Modal Header Wrap */}
                        <div className="relative pt-10 pb-6 px-8 flex flex-col items-center text-center">
                            <button
                                onClick={() => setSelectedBooking(null)}
                                className={`absolute top-5 right-5 p-2 rounded-lg transition-colors
                                    ${isDarkMode ? 'bg-zinc-800 text-zinc-400 hover:text-white' : 'bg-gray-100 text-gray-500 hover:text-gray-900'}`}
                            >
                                <X size={20} />
                            </button>

                            <div className={`w-16 h-16 rounded-xl flex items-center justify-center mb-6
                                ${selectedBooking.status === 'confirmed'
                                    ? 'bg-blue-600 text-white'
                                    : 'bg-amber-600 text-white'}`}>
                                <User size={30} />
                            </div>

                            <span className={`inline-block px-3 py-1 rounded text-[9px] font-bold uppercase tracking-widest mb-3 border
                                ${selectedBooking.status === 'confirmed'
                                    ? (isDarkMode ? 'bg-blue-900/30 text-blue-400 border-blue-500/30' : 'bg-blue-50 text-blue-700 border-blue-200')
                                    : (isDarkMode ? 'bg-amber-900/30 text-amber-500 border-amber-500/30' : 'bg-amber-50 text-amber-800 border-amber-200')}`}>
                                {t(selectedBooking.status)}
                            </span>

                            <h2 className={`text-3xl font-bold tracking-tight mb-1 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                {selectedBooking.prospectName}
                            </h2>
                            <p className="flex items-center gap-2 text-xs font-semibold text-red-600 uppercase tracking-wider">
                                <Building2 size={14} />
                                {t('apartment_display', { number: selectedBooking.apartmentNumber })} • {selectedBooking.buildingName} • {t('floor_display', { number: selectedBooking.floor })}
                            </p>
                        </div>

                        {/* Modal Content Body */}
                        <div className={`px-8 pb-8 space-y-6`}>
                            {/* Date Timeline section */}
                            <div className="flex justify-between items-center py-4 border-y border-zinc-100 dark:border-zinc-800">
                                <div className="text-center">
                                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center mx-auto mb-2 border transition-colors
                                        ${isDarkMode ? 'bg-zinc-800 border-zinc-700 text-zinc-400' : 'bg-gray-50 border-gray-200 text-gray-500'}`}>
                                        <CalendarClock size={20} />
                                    </div>
                                    <p className={`text-[8px] font-bold uppercase tracking-wider mb-1 ${isDarkMode ? 'text-zinc-500' : 'text-gray-400'}`}>{t('move_in')}</p>
                                    <p className={`text-xs font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                        {new Date(selectedBooking.moveInDate).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' })}
                                    </p>
                                </div>

                                <div className="h-px w-12 bg-zinc-200 dark:bg-zinc-800"></div>

                                <div className="text-center">
                                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center mx-auto mb-2 border transition-colors
                                        ${isDarkMode ? 'bg-zinc-800 border-zinc-700 text-zinc-400' : 'bg-gray-50 border-gray-200 text-gray-500'}`}>
                                        <CalendarCheck size={20} />
                                    </div>
                                    <p className={`text-[8px] font-bold uppercase tracking-wider mb-1 ${isDarkMode ? 'text-zinc-500' : 'text-gray-400'}`}>{t('move_out')}</p>
                                    <p className={`text-xs font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                        {selectedBooking.moveOutDate ? new Date(selectedBooking.moveOutDate).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' }) : t('none')}
                                    </p>
                                </div>
                            </div>

                            {/* expiration alert box */}
                            {selectedBooking.status === 'pending' && selectedBooking.expiresAt && (
                                <div className={`p-4 rounded-xl border flex items-center gap-4
                                    ${isDarkMode ? 'bg-zinc-900 border-red-500/20' : 'bg-red-50 border-red-100'}`}>
                                    <div className={`shrink-0 w-10 h-10 rounded-lg flex items-center justify-center ${isDarkMode ? 'bg-red-500/10 text-red-500' : 'bg-white text-red-600 border border-red-100'}`}>
                                        <Clock size={20} />
                                    </div>
                                    <div>
                                        <p className={`text-[8px] font-bold uppercase tracking-wider mb-0.5 ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>{t('hold_expiration')}</p>
                                        <p className={`text-sm font-bold ${isDarkMode ? 'text-white' : 'text-zinc-900'}`}>
                                            {new Date(selectedBooking.expiresAt).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                        </p>
                                    </div>
                                </div>
                            )}

                            {/* Action Buttons */}
                            <div className="grid grid-cols-2 gap-3 pt-2">
                                <button
                                    onClick={() => navigate(`/admin/bookings/${selectedBooking.id}`)}
                                    className="px-6 py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg font-bold flex items-center justify-center gap-2 transition-colors uppercase text-[10px] tracking-wider shadow-sm"
                                >
                                    <ExternalLink size={14} />
                                    {t('view_details')}
                                </button>
                                <button
                                    onClick={() => setSelectedBooking(null)}
                                    className={`px-6 py-3 rounded-lg font-bold border transition-colors uppercase text-[10px] tracking-wider
                                        ${isDarkMode ? 'bg-zinc-800 border-zinc-700 text-white hover:bg-zinc-700' : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'}`}
                                >
                                    {t('close')}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BookingCalendar;
